import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";

import { CommunityGuidelines } from "@/components/CommunityGuidelines";
import { PaymentAgreement } from "@/components/PaymentAgreement";
import { PrivacyPolicy } from "@/components/PrivacyPolicy";
import { Terms } from "@/components/Terms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogTrigger,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  agreements,
  communityGuidelinesAgreement,
  paidServiceAgreement,
  privacyAgreement,
  termsAndConditionsAgreement,
} from "@/lib/agreements";
import {
  acceptLicense,
  AccountDetails,
  getCurrentUserQueryOptions,
} from "@/lib/api";
import { User } from "@/lib/types";

function hasAgreedToAgreement(user: User, agreement: string) {
  return (
    agreements[agreement as keyof typeof agreements] == undefined ||
    user.account?.license_acceptances?.[agreement] ===
      agreements[agreement as keyof typeof agreements]
  );
}

export function needsToAgreeToPaidService(
  user: User,
  accountDetails: AccountDetails | undefined,
) {
  return (
    !hasAgreedToAgreement(user, paidServiceAgreement) &&
    (accountDetails?.can_run_campaigns ||
      accountDetails?.can_spend_on_campaigns)
  );
}

export function needsToAgreeToPrivacy(user: User) {
  return !hasAgreedToAgreement(user, privacyAgreement);
}

export function needsToAgreeToTerms(user: User) {
  return !hasAgreedToAgreement(user, termsAndConditionsAgreement);
}

export function needsToAgreeToCommunityGuidelines(user: User) {
  return !hasAgreedToAgreement(user, communityGuidelinesAgreement);
}

export function CommunityGuidelinesDialog({
  children,
  defaultOpen,
}: {
  children?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const hasAgreed = hasAgreedToAgreement(user, communityGuidelinesAgreement);
  const [dialogOpen, setDialogOpen] = useState(defaultOpen);
  const onAgree = useMutation({
    onSuccess: () => setDialogOpen(false),
    mutationFn: async () => {
      if (agreements[communityGuidelinesAgreement]) {
        await acceptLicense(
          communityGuidelinesAgreement,
          agreements[communityGuidelinesAgreement],
        );
      }
    },
  });

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Community Guidelines</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="notes-content bg-slate-subtle border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 max-h-[50vh] overflow-auto">
          <CommunityGuidelines />
        </div>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => !hasAgreed && onAgree.mutate()}>
            {hasAgreed ? "Close" : "Agree"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function PaymentAgreementDialog({
  children,
  defaultOpen,
}: {
  children?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const hasAgreed = hasAgreedToAgreement(user, paidServiceAgreement);
  const [dialogOpen, setDialogOpen] = useState(defaultOpen);
  const onAgree = useMutation({
    onSuccess: () => setDialogOpen(false),
    mutationFn: async () => {
      if (agreements[paidServiceAgreement]) {
        await acceptLicense(
          paidServiceAgreement,
          agreements[paidServiceAgreement],
        );
      }
    },
  });

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Payment Agreement</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="notes-content bg-slate-subtle border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 max-h-[50vh] overflow-auto">
          <PaymentAgreement />
        </div>
        <p>
          By participating in campaigns on Graze's platform, the Sponsor and
          Curator agree to these terms and conditions.
        </p>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => !hasAgreed && onAgree.mutate()}>
            {hasAgreed ? "Close" : "Agree"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function PrivacyPolicyDialog({
  children,
  defaultOpen,
}: {
  children?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const hasAgreed = hasAgreedToAgreement(user, privacyAgreement);
  const [dialogOpen, setDialogOpen] = useState(defaultOpen);
  const onAgree = useMutation({
    onSuccess: () => setDialogOpen(false),
    mutationFn: async () => {
      if (agreements[privacyAgreement]) {
        await acceptLicense(privacyAgreement, agreements[privacyAgreement]);
      }
    },
  });

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Privacy Policy</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="notes-content bg-slate-subtle border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 max-h-[50vh] overflow-auto">
          <PrivacyPolicy />
        </div>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => !hasAgreed && onAgree.mutate()}>
            {hasAgreed ? "Close" : "Agree"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function TermsAndConditionsDialog({
  children,
  defaultOpen,
}: {
  children?: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const { data: user } = useSuspenseQuery(getCurrentUserQueryOptions());
  const hasAgreed = hasAgreedToAgreement(user, termsAndConditionsAgreement);
  const [dialogOpen, setDialogOpen] = useState(defaultOpen);
  const onAgree = useMutation({
    onSuccess: () => setDialogOpen(false),
    mutationFn: async () => {
      if (agreements[termsAndConditionsAgreement]) {
        await acceptLicense(
          termsAndConditionsAgreement,
          agreements[termsAndConditionsAgreement],
        );
      }
    },
  });

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Terms & Conditions</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="notes-content bg-slate-subtle border border-slate-6 dark:border-slatedark-6 rounded-lg p-4 max-h-[50vh] overflow-auto">
          <Terms />
        </div>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => !hasAgreed && onAgree.mutate()}>
            {hasAgreed ? "Close" : "Agree"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
